/**
 * Mega menu hover intent — diagonal travel to panel without accidental
 * switching, while horizontal moves between nav items switch normally.
 */
class MegaMenuController {
  constructor(nav) {
    this.nav = nav;
    this.items = [...nav.querySelectorAll('.header__nav-item--has-dropdown')];
    this.activeItem = null;
    this.pendingItem = null;
    this.openTimer = null;
    this.closeTimer = null;
    this.openDelay = 90;
    this.closeDelay = 260;
    this.pointer = { x: 0, y: 0 };
    this.previousPointer = { x: 0, y: 0 };

    this.onDocumentMove = (event) => {
      this.previousPointer = { ...this.pointer };
      this.pointer = { x: event.clientX, y: event.clientY };

      if (this.activeItem && this.closeTimer && this.isMovingToActivePanel(this.activeItem)) {
        this.clearCloseTimer();
      }
    };

    this.items.forEach((item) => {
      item.addEventListener('mouseenter', () => this.onItemEnter(item));
      item.addEventListener('mouseleave', (event) => this.onItemLeave(item, event));

      const panel = item.querySelector('.mega-menu');
      if (!panel) return;

      panel.addEventListener('mouseenter', () => this.onPanelEnter(item));
      panel.addEventListener('mouseleave', (event) => this.onPanelLeave(item, event));
    });

    nav.addEventListener('mouseleave', () => this.scheduleClose());
  }

  connect() {
    document.addEventListener('mousemove', this.onDocumentMove);
  }

  disconnect() {
    document.removeEventListener('mousemove', this.onDocumentMove);
    this.clearTimers();
    this.setOpenItem(null);
  }

  clearOpenTimer() {
    clearTimeout(this.openTimer);
    this.openTimer = null;
    this.pendingItem = null;
  }

  clearCloseTimer() {
    clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }

  clearTimers() {
    this.clearOpenTimer();
    this.clearCloseTimer();
  }

  scheduleOpen(item) {
    this.clearCloseTimer();

    if (this.activeItem === item) return;

    if (!this.activeItem) {
      this.setOpenItem(item);
      return;
    }

    this.clearOpenTimer();
    this.pendingItem = item;
    this.openTimer = window.setTimeout(() => {
      this.setOpenItem(item);
    }, this.openDelay);
  }

  cancelPendingOpen(item) {
    if (this.pendingItem !== item) return;
    this.clearOpenTimer();
  }

  scheduleClose() {
    this.clearCloseTimer();

    this.closeTimer = window.setTimeout(() => {
      if (this.activeItem && this.isMovingToActivePanel(this.activeItem)) return;
      this.setOpenItem(null);
    }, this.closeDelay);
  }

  onItemEnter(item) {
    this.scheduleOpen(item);
  }

  onItemLeave(item, event) {
    this.cancelPendingOpen(item);

    const panel = item.querySelector('.mega-menu');
    const next = event.relatedTarget;

    if (next && (item.contains(next) || (panel && panel.contains(next)))) {
      return;
    }

    const nextNavItem = next && next.closest('.header__nav-item--has-dropdown');
    if (nextNavItem && nextNavItem !== item) {
      return;
    }

    if (this.activeItem === item && this.isMovingToActivePanel(item)) {
      return;
    }

    if (this.activeItem === item) {
      this.scheduleClose();
    }
  }

  onPanelEnter(item) {
    this.clearTimers();
    this.setOpenItem(item);
  }

  onPanelLeave(item, event) {
    const next = event.relatedTarget;
    if (next && item.contains(next)) return;

    const nextNavItem = next && next.closest('.header__nav-item--has-dropdown');
    if (nextNavItem && nextNavItem !== item) {
      this.scheduleOpen(nextNavItem);
      return;
    }

    if (this.activeItem === item) {
      this.scheduleClose();
    }
  }

  setOpenItem(item) {
    this.clearTimers();

    this.items.forEach((navItem) => {
      const isOpen = navItem === item;
      navItem.classList.toggle('mega-menu-open', isOpen);

      const trigger = navItem.querySelector('.header__menu-item[aria-controls]');
      if (trigger) {
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }
    });

    this.activeItem = item;
  }

  getNavItemAtPointer() {
    for (const item of this.items) {
      if (this.pointInRect(this.pointer.x, this.pointer.y, item.getBoundingClientRect())) {
        return item;
      }
    }
    return null;
  }

  isMovingToActivePanel(item) {
    const hoveredNavItem = this.getNavItemAtPointer();
    if (hoveredNavItem && hoveredNavItem !== item) {
      return false;
    }

    const panel = item.querySelector('.mega-menu');
    if (!panel) return false;

    const itemRect = item.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const lane = {
      left: itemRect.left - 14,
      right: itemRect.right + 14,
      top: itemRect.top,
      bottom: panelRect.bottom,
    };

    if (!this.pointInRect(this.pointer.x, this.pointer.y, lane)) {
      return false;
    }

    const movingDown = this.pointer.y >= this.previousPointer.y - 3;
    const belowNav = this.pointer.y >= itemRect.bottom - 6;

    return movingDown || belowNav;
  }

  pointInRect(x, y, rect) {
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
  }
}

class MegaMenuNav extends HTMLElement {
  connectedCallback() {
    this.mediaQuery = window.matchMedia('(min-width: 990px)');
    this.controller = null;

    this.onMediaChange = () => this.sync();
    this.mediaQuery.addEventListener('change', this.onMediaChange);
    this.sync();
  }

  disconnectedCallback() {
    this.mediaQuery?.removeEventListener('change', this.onMediaChange);
    this.controller?.disconnect();
    this.controller = null;
  }

  sync() {
    if (this.mediaQuery.matches) {
      if (!this.controller) {
        this.controller = new MegaMenuController(this);
        this.controller.connect();
      }
      return;
    }

    this.controller?.disconnect();
    this.controller = null;
  }
}

customElements.define('mega-menu-nav', MegaMenuNav);
